<template>
  <div class="w-full rounded-lg shadow bg-white">
    <!-- Premium enterprise header with filter controls -->
    <div class="bg-white border-b border-gray-200">
      <!-- Main header with controls -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 sm:p-4">
        <!-- Right section: Controls -->
        <div class="flex flex-wrap items-center gap-4 sm:gap-6">
          <!-- Filter indicator when active -->
          <div v-if="showUnique" class="flex items-center text-sm text-[#157813]">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span class="font-medium">Filtered</span>
          </div>
          
          <!-- Enhanced toggle with premium styling -->
          <div class="flex items-center flex-wrap gap-2">
            <span class="text-sm font-medium text-gray-700">Show unique queries only</span>
            <div class="relative flex items-center">
              <label 
                for="unique-toggle" 
                class="relative inline-flex items-center h-6 cursor-pointer"
              >
                <div class="relative w-12 h-6">
                  <input
                    type="checkbox"
                    class="sr-only peer"
                    id="unique-toggle"
                    v-model="showUnique"
                    @change="$emit('toggle-unique', showUnique)"
                  />
                  <div class="w-full h-full bg-gray-200 rounded-full peer peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-[#157813]/30 peer-checked:bg-[#157813] transition-colors duration-200">
                    <!-- ON label -->
                    <span class="absolute left-1.5 top-1/2 -translate-y-1/2 text-[9px] font-bold text-white opacity-0 peer-checked:opacity-100 transition-all duration-200 select-none">ON</span>
                    <!-- OFF label -->
                    <span class="absolute right-1.5 top-1/2 -translate-y-1/2 text-[9px] font-bold text-gray-500 opacity-100 peer-checked:opacity-0 transition-all duration-200 select-none">OFF</span>
                  </div>
                  <div class="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-all duration-200 transform peer-checked:translate-x-6 peer-checked:bg-white shadow-sm"></div>
                </div>
              </label>
              
              <!-- Active status badge -->
              <div v-if="showUnique" class="ml-2 flex h-5 items-center">
                <span class="relative flex h-2 w-2">
                  <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#157813] opacity-50"></span>
                  <span class="relative inline-flex rounded-full h-2 w-2 bg-[#157813]"></span>
                </span>
              </div>
            </div>
          </div>
          
          <!-- Info tooltip -->
          <div class="relative group">
            <button type="button" class="flex items-center justify-center h-6 w-6 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#157813]">
              <span class="sr-only">Help information</span>
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <div class="absolute z-10 w-64 p-3 text-xs bg-gray-800 text-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 transform group-hover:translate-y-0 translate-y-1 right-0 top-8">
              <p class="font-medium mb-1">Filter Unique Queries</p>
              <p class="text-gray-300"> When enabled, only the latest unique queries based on text content are displayed</p>
              <div class="absolute right-2 top-0 w-2 h-2 bg-gray-800 transform rotate-45 -translate-y-1"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Enhanced table with improved sorting indicators -->
    <div class="w-full overflow-x-auto">
      <table class="w-full table-fixed divide-y divide-gray-200">
        <thead class="bg-[#157813]">
          <tr>
            <th
              v-for="header in headers"
              :key="header.key"
              @click="$emit('sort', header.key)"
              class="px-2 py-3 sm:px-3 text-left text-xs sm:text-sm font-medium text-white uppercase tracking-wider whitespace-nowrap cursor-pointer select-none group transition-colors duration-150 hover:bg-[#126810]"
              :class="{
                'w-[80px] min-w-[80px]': ['id', 'neuronId', 'extraction', 'queryStartTime'].includes(header.key),
                'w-[120px] min-w-[120px]': header.key === 'author',
                'w-[250px] min-w-[250px] max-w-[400px]': header.key === 'neuronText'
              }"
            >
              <div class="flex items-center justify-center">
                {{ header.label }}
                <span class="inline-block ml-1 transition-opacity duration-150" :class="{'opacity-100': sortState.key === header.key, 'opacity-60 group-hover:opacity-100': sortState.key !== header.key}">
                  <template v-if="sortState.key === header.key && sortState.direction === 'asc'">
                    <!-- Ascending Icon -->
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clip-rule="evenodd" />
                    </svg>
                  </template>
                  <template v-else-if="sortState.key === header.key && sortState.direction === 'desc'">
                    <!-- Descending Icon -->
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </template>
                  <template v-else>
                    <!-- Default Sort Icon -->
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                    </svg>
                  </template>
                </span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr
            v-for="query in queries"
            :key="query.id"
            @click="$emit('row-click', query.id)"
            class="hover:bg-gray-50 text-center cursor-pointer active:bg-gray-100 transition-colors"
          >
            <td class="px-2 text-center sm:px-4 py-2 text-xs sm:text-sm text-gray-500 whitespace-nowrap truncate">
              {{ query.id }}
            </td>
            <td class="px-2 sm:px-4 py-2 text-xs sm:text-sm text-gray-500 whitespace-nowrap truncate">
              {{ query.neuronId }}
            </td>
            <td class="px-2 sm:px-4 py-2 text-xs sm:text-sm text-gray-500 overflow-hidden">
              <div class="line-clamp-2 break-all">{{ query.neuronText }}</div>
            </td>
            <td class="px-2 text-center sm:px-4 py-2 text-xs sm:text-sm text-gray-500 whitespace-nowrap truncate">
              {{ query.author }}
            </td>
            <td class="px-2 sm:px-4  py-2 text-xs sm:text-sm text-gray-500 text-center whitespace-nowrap">
              {{ query.extraction }}
            </td>
            <td class="px-2 text-center sm:px-4 py-2 text-xs sm:text-sm text-gray-500 whitespace-nowrap">
              N/A
            </td>
          </tr>
          <tr v-if="queries.length === 0">
            <td
              colspan="5"
              class="px-4 py-4 text-center text-sm text-gray-500"
            >
              <div class="flex flex-col items-center justify-center py-6">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-gray-300 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>No queries found matching your criteria</p>
                <button 
                  @click="$emit('toggle-unique', false)" 
                  class="mt-2 text-[#157813] hover:text-[#126810] text-sm font-medium focus:outline-none focus:underline"
                >
                  Show all queries instead
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Query } from "@/models/Query";
import { onMounted, ref, watch } from 'vue';

interface TableHeader {
  key: string;
  label: string;
  searchable?: boolean;
}

interface Props {
  queries: Query[];
  headers: TableHeader[];
  sortState: {
    key: string;
    direction: "asc" | "desc";
  };
}

const props = defineProps<Props>();
const showUnique = ref(true);

// Emit toggle event on mount and when toggle changes
onMounted(() => {
  emit('toggle-unique', true);
});

watch(showUnique, (newValue) => {
  emit('toggle-unique', newValue);
});

const emit = defineEmits<{
  (e: 'sort', key: string): void;
  (e: 'row-click', id: string): void;
  (e: 'toggle-unique', show: boolean): void;
}>();
</script>

<style scoped>
/* Remove media query since we're handling responsiveness with Tailwind */
/* Add these utility classes */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Keep existing styles for toggle switch and animations */
/* Premium enterprise styling */
.group:hover .group-hover\:opacity-100 {
  opacity: 1;
}

.group:hover .group-hover\:visible {
  visibility: visible;
}

.group:hover .group-hover\:translate-y-0 {
  transform: translateY(0);
}

/* Active toggle status pulse animation */
@keyframes ping {
  75%, 100% {
    transform: scale(2);
    opacity: 0;
  }
}

.animate-ping {
  animation: ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite;
}

/* Table hover effect */
th:hover {
  background-color: #126810;
}

/* Enhanced scrollbar styling */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f3f4f6;
  border-radius: 5px;
}

::-webkit-scrollbar-thumb {
  background: #157813;
  border-radius: 5px;
}

::-webkit-scrollbar-thumb:hover {
  background: #126810;
}

/* Add these styles for the toggle switch */
input:checked + label div span:first-child {
  opacity: 1;
}

input:checked + label div span:last-child {
  opacity: 0;
}

input:not(:checked) + label div span:first-child {
  opacity: 0;
}

input:not(:checked) + label div span:last-child {
  opacity: 1;
}
</style>